import { Home, Wallet, ClockIcon, Scan, MoreHorizontal } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useState } from "react";
import { UniversalQRScanner } from "@/components/UniversalQRScanner";
import { MoreMenuSheet } from "@/components/MoreMenuSheet";
import { QRCodeType } from "@/utils/qrCodeDetector";

const getBottomNavItems = (t: (key: string) => string) => [
  { 
    title: t('bottomNav.home'), 
    url: "/dashboard", 
    icon: Home,
    activeIcon: Home
  },
  { 
    title: t('bottomNav.wallet'), 
    url: "/wallet-management", 
    icon: Wallet,
    activeIcon: Wallet
  },
  { 
    title: t('bottomNav.scanQR'), 
    url: "qr-scanner", // Special identifier for modal
    icon: Scan,
    activeIcon: Scan,
    isCenter: true
  },
  { 
    title: t('bottomNav.history'), 
    url: "/transaction-history", 
    icon: ClockIcon,
    activeIcon: ClockIcon
  },
  { 
    title: t('bottomNav.more'), 
    url: "more-menu", // Special identifier for more menu
    icon: MoreHorizontal,
    activeIcon: MoreHorizontal
  }
];

export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  
  // Get language context with error handling
  let t: (key: string) => string;
  try {
    const context = useLanguage();
    t = context.t;
  } catch (error) {
    console.error('LanguageContext not available in MobileBottomNav, using fallback translations');
    // Fallback translation function
    t = (key: string) => {
      const fallbacks: Record<string, string> = {
        'bottomNav.home': 'Home',
        'bottomNav.wallet': 'Wallet',
        'bottomNav.scanQR': 'QR',
        'bottomNav.history': 'History',
        'bottomNav.more': 'More'
      };
      return fallbacks[key] || key;
    };
  }
  
  const [clickedItem, setClickedItem] = useState<string | null>(null);
  const [ripplePosition, setRipplePosition] = useState({ x: 0, y: 0 });
  
  const bottomNavItems = getBottomNavItems(t);

  const handleClick = (url: string, event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    // Handle QR Scanner modal
    if (url === 'qr-scanner') {
      event.preventDefault();
      setShowQRScanner(true);
      return;
    }

    // Handle More Menu
    if (url === 'more-menu') {
      event.preventDefault();
      setShowMoreMenu(true);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    setRipplePosition({ x, y });
    setClickedItem(url);
    
    // Reset animation after completion
    setTimeout(() => setClickedItem(null), 600);
  };

  const handleQRResult = (type: QRCodeType, data: any) => {
    switch (type) {
      case 'p2p_transfer':
        navigate('/p2p-transfer', { state: { qrData: data } });
        break;
      case 'payment_gateway':
        if (data.payment_id) {
          navigate(`/pay/${data.payment_id}`);
        } else {
          navigate('/qr-payment', { state: { qrData: data } });
        }
        break;
      case 'receipt':
        navigate(data.url);
        break;
      case 'deposit_qr':
        navigate('/deposit', { state: { bankQR: data } });
        break;
    }
  };

  // Don't show on auth pages / admin pages / chat pages
  if (
    location.pathname === '/auth' ||
    location.pathname === '/auth-2fa' ||
    location.pathname === '/' ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/chat/')
  ) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-t border-border/50 safe-area-pb shadow-lg">
        <div className="flex items-center justify-around h-16 px-1">
          {bottomNavItems.map((item) => {
            const isActive = location.pathname === item.url;
            const Icon = isActive ? item.activeIcon : item.icon;
            const isClicked = clickedItem === item.url;
            const isSpecial = item.url === 'qr-scanner' || item.url === 'more-menu';
            const isCenter = (item as any).isCenter;
            
            // Center QR button with special styling
            if (isCenter) {
              return (
                <button
                  key={item.url}
                  onClick={(e) => handleClick(item.url, e)}
                  className={cn(
                    "relative flex flex-col items-center justify-center -mt-6",
                    "transition-all duration-300 ease-out transform-gpu",
                    "active:scale-95"
                  )}
                >
                  <div className={cn(
                    "w-14 h-14 rounded-full flex items-center justify-center",
                    "bg-primary shadow-lg shadow-primary/30",
                    "transition-all duration-200"
                  )}>
                    <Icon className="h-7 w-7 text-primary-foreground" />
                  </div>
                  <span className="text-[10px] font-medium mt-1 text-muted-foreground">
                    {item.title}
                  </span>
                </button>
              );
            }
            
            // Regular or special nav items
            if (isSpecial) {
              return (
                <button
                  key={item.url}
                  onClick={(e) => handleClick(item.url, e)}
                  className={cn(
                    "relative overflow-hidden flex flex-col items-center justify-center min-w-0 flex-1 py-2 px-1 rounded-xl",
                    "transition-all duration-300 ease-out transform-gpu",
                    "hover:bg-muted/50 hover:scale-105 active:scale-95",
                    "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Icon className="h-6 w-6 mb-1 transition-all duration-500 ease-out transform-gpu" />
                  <span className="text-[10px] font-medium truncate transition-all duration-500 ease-out leading-tight max-w-full">
                    {item.title}
                  </span>
                </button>
              );
            }
            
            return (
              <NavLink
                key={item.url}
                to={item.url}
                onClick={(e) => handleClick(item.url, e)}
                className={cn(
                  "relative overflow-hidden flex flex-col items-center justify-center min-w-0 flex-1 py-2 px-1 rounded-xl",
                  "transition-all duration-300 ease-out transform-gpu",
                  "hover:bg-muted/50 hover:scale-105 active:scale-95",
                  isClicked && "animate-bounce",
                  isActive 
                    ? "text-primary bg-gradient-to-t from-primary/20 to-primary/10 shadow-lg scale-105 border border-primary/20" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {/* Ripple Effect */}
                {isClicked && (
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      left: ripplePosition.x - 20,
                      top: ripplePosition.y - 20,
                    }}
                  >
                    <div className="w-10 h-10 bg-primary/30 rounded-full animate-ping" />
                  </div>
                )}
                
                <Icon 
                  className={cn(
                    "h-6 w-6 mb-1 transition-all duration-500 ease-out transform-gpu",
                    isActive && "drop-shadow-md animate-pulse scale-110",
                    isClicked && "animate-bounce scale-125"
                  )} 
                />
                <span className={cn(
                  "text-[10px] font-medium truncate transition-all duration-500 ease-out leading-tight max-w-full",
                  isActive ? "text-primary font-bold scale-105" : "text-muted-foreground",
                  isClicked && "animate-pulse"
                )}>
                  {item.title}
                </span>
              </NavLink>
            );
          })}
        </div>
      </div>

      {/* Universal QR Scanner Modal */}
      <UniversalQRScanner
        isOpen={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onResult={handleQRResult}
        currentContext="any"
      />

      {/* More Menu Sheet */}
      <MoreMenuSheet
        isOpen={showMoreMenu}
        onClose={() => setShowMoreMenu(false)}
      />
    </>
  );
}
